// Día de negocio (jornada) del restaurante.
//
// Toda la app usa UNA hora de inicio de jornada: `user.arqueoCutoffHour`
// (Ajustes › Caja/arqueos). Valores válidos 1..23; 0, null o cualquier otro
// valor → 4 (las 00:00 ya no se pueden elegir).
//
// «Hoy» va de las h:00 de hoy a las h:00 de mañana (hora local). Antes de las
// h:00, el «hoy» de negocio sigue siendo AYER (un cierre a las 02:00 cuenta en
// la jornada anterior).
//
// El API recibe FECHAS de negocio (yyyy-MM-dd) en desde/hasta y aplica la hora
// por su cuenta: el Front solo tiene que enviar la fecha de negocio correcta.
import { useContext, useEffect, useState } from "react";
import { ReactReduxContext } from "react-redux";
import { localISODate } from "./localDate";

export const DEFAULT_BUSINESS_DAY_START = 4;

export function effectiveStartHour(h) {
  if (h === null || h === undefined || h === "") return DEFAULT_BUSINESS_DAY_START;
  const n = Number(h);
  if (!Number.isInteger(n) || n < 1 || n > 23) return DEFAULT_BUSINESS_DAY_START;
  return n;
}

function toDate(now) {
  if (now instanceof Date) return new Date(now.getTime());
  if (now === null || now === undefined) return new Date();
  return new Date(now);
}

function midnight(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d, n) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Fecha (00:00 local) del día de negocio en curso en el instante `now`. */
export function businessToday(now = new Date(), h) {
  const d = toDate(now);
  const start = effectiveStartHour(h);
  const day = midnight(d);
  return d.getHours() < start ? addDays(day, -1) : day;
}

/** Día de negocio al que pertenece un instante cualquiera (p. ej. un ticket). */
export const businessDayOf = (instant, h) => businessToday(instant, h);

/** yyyy-MM-dd (local) del día de negocio en curso. */
export function businessTodayISO(h, now = new Date()) {
  return localISODate(businessToday(now, h));
}

/** Día de negocio de hace `n` días (n=1 → ayer). */
export function businessDaysAgo(n, h, now = new Date()) {
  return addDays(businessToday(now, h), -n);
}

/** Instantes reales [inicio, fin) de un día de negocio (fecha local). */
export function businessDayBounds(day, h) {
  const start = effectiveStartHour(h);
  const d = midnight(toDate(day));
  return {
    start: new Date(d.getFullYear(), d.getMonth(), d.getDate(), start, 0, 0, 0),
    end: new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, start, 0, 0, 0),
  };
}

/** Instantes [inicio, fin) del día de negocio en curso. */
export function currentBusinessDayBounds(h, now = new Date()) {
  return businessDayBounds(businessToday(now, h), h);
}

// ── Presets de rango (fechas de negocio; desde/hasta inclusivos) ──────────

export function businessTodayRange(h, now = new Date()) {
  const d = businessToday(now, h);
  return { desde: d, hasta: d };
}

export function businessYesterdayRange(h, now = new Date()) {
  const d = businessDaysAgo(1, h, now);
  return { desde: d, hasta: d };
}

/** Últimos N días de negocio incluyendo hoy (N=7 → hoy y los 6 anteriores). */
export function businessLastNDaysRange(n, h, now = new Date()) {
  const hasta = businessToday(now, h);
  return { desde: addDays(hasta, -(Math.max(1, n) - 1)), hasta };
}

/** Semana (lunes–domingo) que contiene el día de negocio de hoy. */
export function businessThisWeekRange(h, now = new Date()) {
  const today = businessToday(now, h);
  const dow = (today.getDay() + 6) % 7; // lunes = 0
  const desde = addDays(today, -dow);
  return { desde, hasta: addDays(desde, 6) };
}

export function businessLastWeekRange(h, now = new Date()) {
  const { desde } = businessThisWeekRange(h, now);
  const prev = addDays(desde, -7);
  return { desde: prev, hasta: addDays(prev, 6) };
}

/** Mes natural (1..último) que contiene el día de negocio de hoy. */
export function businessThisMonthRange(h, now = new Date()) {
  const today = businessToday(now, h);
  return {
    desde: new Date(today.getFullYear(), today.getMonth(), 1),
    hasta: new Date(today.getFullYear(), today.getMonth() + 1, 0),
  };
}

export function businessLastMonthRange(h, now = new Date()) {
  const today = businessToday(now, h);
  return {
    desde: new Date(today.getFullYear(), today.getMonth() - 1, 1),
    hasta: new Date(today.getFullYear(), today.getMonth(), 0),
  };
}

/** Mes en curso hasta hoy (1 → hoy de negocio). */
export function businessMonthToDateRange(h, now = new Date()) {
  const today = businessToday(now, h);
  return { desde: new Date(today.getFullYear(), today.getMonth(), 1), hasta: today };
}

/** Año natural del día de negocio de hoy (útil para «año en curso»). */
export function businessYear(h, now = new Date()) {
  return businessToday(now, h).getFullYear();
}

export function toISORange(range) {
  return { desde: localISODate(range.desde), hasta: localISODate(range.hasta) };
}

// ── Franjas horarias y etiquetas de la jornada ─────────────────────────────
//
// 2026-10-20 · El API devuelve las 24 franjas de «Hoy»/«Ayer» (y del balance
// de caja) indexadas por hora LOCAL 0..23. Las gráficas las pintan en orden
// de jornada: [h, h+1, …, 23, 0, …, h-1], con etiquetas «03:00»…«02:00».
// Antes el eje era fijo («1…24» o «6:00…6:00») y no coincidía con la jornada.

const pad2 = (n) => String(n).padStart(2, "0");

/** Horas locales de la jornada en orden: [h, …, 23, 0, …, h-1]. */
export function businessHourOrder(h) {
  const start = effectiveStartHour(h);
  return Array.from({ length: 24 }, (_, i) => (start + i) % 24);
}

/** Etiquetas «HH:00» de las 24 franjas en orden de jornada. */
export function businessHourLabels(h) {
  return businessHourOrder(h).map((x) => `${pad2(x)}:00`);
}

/**
 * Reordena una serie de 24 valores indexada por hora local (0..23) al orden
 * de la jornada. Si no tiene 24 valores (semana, mes…) se devuelve tal cual.
 */
export function rotateToBusinessOrder(values, h) {
  if (!Array.isArray(values) || values.length !== 24) return values;
  return businessHourOrder(h).map((hour) => values[hour]);
}

function fmtDayMonth(d) {
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function parseBusinessDate(value) {
  if (value instanceof Date) return midnight(value);
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return midnight(toDate(value));
}

/**
 * Texto de la ventana real de unos días de negocio (ambos incluidos):
 * «20/10/2026 03:00 → 21/10/2026 03:00». Acepta Date o «yyyy-MM-dd».
 */
export function formatBusinessWindow(desde, hasta, h) {
  const from = businessDayBounds(parseBusinessDate(desde), h).start;
  const to = businessDayBounds(parseBusinessDate(hasta ?? desde), h).end;
  const hh = `${pad2(effectiveStartHour(h))}:00`;
  return `${fmtDayMonth(from)} ${hh} → ${fmtDayMonth(to)} ${hh}`;
}

// ── Redux ─────────────────────────────────────────────────────────────────

export function selectBusinessDayStartHour(state) {
  return effectiveStartHour(state?.authJwt?.user?.arqueoCutoffHour);
}

/**
 * Hora de inicio de jornada efectiva del usuario logueado (1..23, por
 * defecto 4). Tolera montarse fuera de un <Provider> (tests de vistas
 * sueltas): en ese caso devuelve el valor por defecto.
 */
export function useBusinessDayStartHour() {
  const store = useContext(ReactReduxContext)?.store;
  const [hour, setHour] = useState(() => selectBusinessDayStartHour(store?.getState?.()));
  useEffect(() => {
    if (!store) return undefined;
    const update = () => setHour(selectBusinessDayStartHour(store.getState()));
    update();
    return store.subscribe(update);
  }, [store]);
  return hour;
}
